import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowUpRight, HeartHandshake, Play, X } from 'lucide-react';
import { useLocale } from '../../i18n/I18n';
import { usePrivacy } from '../privacy/PrivacyCenter';
import './purpose-video.css';

// Memory only: no tracking or additional browser storage. A new page load can
// invite the visitor again; route changes do not repeat the same announcement.
const announced = new Set();

export function PurposeVideoAnnouncement({ content, preview, open, onWatch }) {
  const { t } = useLocale();
  const { bannerVisible, modalOpen } = usePrivacy();
  const [visible, setVisible] = useState(false);
  const id = `${preview ? 'preview' : 'video'}:${content.announcement?.id || content.video}`;
  const enabled = content.announcement?.enabled && (content.video || preview);
  const blocked = modalOpen || (bannerVisible && !content.announcement?.autoOpen);
  useEffect(() => {
    if (!enabled || blocked) { setVisible(false); return undefined; }
    if (announced.has(id)) return undefined;
    let timer;
    const invite = () => {
      // A first-party video does not require optional-cookie consent. Avoid
      // interrupting a dialog or a visitor already typing.
      if (document.hidden || document.querySelector('[role="dialog"]') || /^(INPUT|TEXTAREA|SELECT)$/.test(document.activeElement?.tagName)) { timer = setTimeout(invite, 1000); return; }
      announced.add(id);
      if (content.announcement?.autoOpen && content.video) onWatch();
      else setVisible(true);
    };
    timer = setTimeout(invite, content.announcement?.delayMs ?? 8000);
    return () => clearTimeout(timer);
  }, [enabled, blocked, id, content.announcement?.delayMs, content.announcement?.autoOpen, content.video, onWatch]);
  useEffect(() => {
    if (!visible) return undefined;
    document.body.classList.add('bz-purpose-invitation-visible');
    return () => document.body.classList.remove('bz-purpose-invitation-visible');
  }, [visible]);
  if (!visible) return null;
  return <aside className="bz-purpose-announcement" hidden={open} aria-label={t('Invitación al video de Bezalel')}>
    <button className="bz-purpose-invitation-close" type="button" onClick={() => setVisible(false)} aria-label={t('Cerrar invitación al video')}><X size={16}/></button>
    <div className="bz-purpose-invitation-art" aria-hidden="true">{content.poster ? <img src={content.poster} alt=""/> : <HeartHandshake size={35} strokeWidth={1.3}/>}<span><Play size={12} fill="currentColor"/></span></div>
    <div className="bz-purpose-invitation-copy"><p className="bz-purpose-invitation-label">{t('Nuestro compromiso social')}</p><h2>{t('Una historia que nos une.')}</h2><p role="status">{t('Conoce nuestro propósito y el apoyo a La Guajira.')}</p><button type="button" onClick={onWatch}>{t('Ver video')}<ArrowUpRight size={16}/></button></div>
  </aside>;
}

export function PurposeVideoDialog({ content, preview, onClose }) {
  const { t } = useLocale();
  const dialog = useRef(null);
  const video = useRef(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const close = useCallback(onClose, [onClose]);
  useEffect(() => {
    const previous = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Announce the dialog without making the close button look selected on
    // touch devices. Keyboard users still get visible focus on its controls.
    dialog.current?.focus({ preventScroll: true });
    const keydown = event => {
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(); }
      if (event.key !== 'Tab') return;
      const nodes = Array.from(dialog.current.querySelectorAll('button:not([disabled]), a[href], video[controls], [tabindex="0"]'));
      const first = nodes[0], last = nodes[nodes.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialog.current)) { event.preventDefault(); first?.focus(); }
    };
    document.addEventListener('keydown', keydown, true);
    return () => { document.body.style.overflow = overflow; document.removeEventListener('keydown', keydown, true); if (previous?.isConnected) previous.focus(); };
  }, [close]);
  useEffect(() => {
    const media = video.current;
    if (media) {
      media.defaultMuted = false;
      media.muted = false;
    }
    return () => media?.pause();
  }, [attempt]);
  return createPortal(<div className="bz-purpose-video-overlay" onClick={event => { if (event.target === event.currentTarget) close(); }}>
    <section ref={dialog} className="bz-purpose-video-dialog" role="dialog" tabIndex={-1} aria-modal="true" aria-labelledby="bz-purpose-video-title">
      <header><div><span>{t('Nuestro compromiso social')} · {content.location}</span><h2 id="bz-purpose-video-title">{t('Más que una empresa, un Propósito.')}</h2></div><button type="button" onClick={close} aria-label={t('Cerrar video')}><X size={21}/></button></header>
      {content.video ? <div className="bz-purpose-player"><video ref={video} key={attempt} controls tabIndex={0} playsInline preload="metadata" poster={content.poster || undefined} aria-label={t('Video del compromiso social de Bezalel')} onError={() => setFailed(true)}><source src={content.video} type="video/mp4" onError={() => setFailed(true)}/>{content.captions && <track kind="captions" src={content.captions} srcLang="es" label="Español" default/>}</video>{failed && <div className="bz-purpose-video-error" role="status"><p>{t('No pudimos cargar el video. Puedes intentarlo de nuevo.')}</p><button type="button" onClick={() => { setFailed(false); setAttempt(value => value + 1); }}>{t('Reintentar')}</button></div>}</div> : <div className="bz-purpose-video-demo"><span className="bz-purpose-demo-tag">{t('Vista previa')}</span><div className="bz-purpose-demo-glow" aria-hidden="true"/><img src="/assets/logo-blue.png" alt="Bezalel Technology" width="230" height="98"/><div className="bz-purpose-demo-heart" aria-hidden="true"><HeartHandshake size={64} strokeWidth={1.3}/></div><p>{t('Más que una empresa, somos el reflejo de un Propósito.')}</p><span>{t('El video real de La Guajira aparecerá aquí.')}</span></div>}
      <footer><p>{t(preview && !content.video ? 'Este ejemplo muestra cómo se abrirá la historia. Aún falta incorporar el video real.' : 'Tecnología con propósito. Compromiso con las personas.')}</p>{content.video && content.transcript && <a href={content.transcript}>{t('Leer transcripción del video')}</a>}<div className="bz-purpose-video-actions"><button type="button" onClick={close}>{t('Seguir explorando')}</button></div></footer>
    </section>
  </div>, document.body);
}
